/* eslint-disable max-lines -- one line over with the feedback gate; this root is locked, so there is no new module to extract into */
import { useIsVoiceDictationModeEnabled } from '@/features/flags';
import { cn } from '@/lib/cn';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import {
  downloadVoiceDebugDump,
  formatVoiceAssistantError,
  formatVoiceDebugDump,
  formatVoiceToolLabel,
  voiceAssistantMessages as messages,
  panelMotionProps,
  shouldAutoDumpVoiceSession,
  statusDotClass,
  useVoiceDockDrag,
  useVoiceWorkspace,
  VoiceAssistantComposer,
  VoiceAssistantEndDialog,
  VoiceAssistantTurnStatus,
  VoiceDictationBar,
  VoiceEvidenceCard,
  voiceWorkspaceMessages,
} from '@heidi/agents';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { ChevronDown, ChevronsUpDown, Download, Hand, X } from 'lucide-react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { VoiceFeedbackThumbs } from '../feedback/VoiceFeedbackThumbs';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import { shouldPaint, useVoicePaintShell } from '../paint';
import { VoiceAssistantApprovalTopBar } from './VoiceAssistantApprovalTopBar';
import { VoiceMicPermissionDenied } from './VoiceMicPermissionDenied';

/** Bottom dock: Talk-to-Heidi control chrome. Evidence keypoints and
 * approvals float above the pill; tool activity is a compact status hint. */
export function VoiceAssistantPanel() {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();
  const {
    isOpen,
    connection,
    sessionState,
    muted,
    assistantSpeaking,
    greetingComplete,
    tools,
    timeline,
    approval,
    dictation,
    error,
    micStream,
    close,
    minimize,
    toggleMute,
    sendText,
    resolveApproval,
    toggleDictation,
  } = useVoiceAssistant();
  const dictationEnabled = useIsVoiceDictationModeEnabled();

  const errorText = error === null ? null : formatVoiceAssistantError(error, intl);
  const isActive = connection === 'active';
  // A live (or connecting) session owns the mic, so ending is destructive and
  // irreversible — gate it behind a confirm. Idle/errored sessions have nothing
  // live to lose, so End closes straight away (AGE-1622).
  const hasLiveSession = connection === 'active' || connection === 'connecting';
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  const handleEndClick = () => (hasLiveSession ? setConfirmEndOpen(true) : close());
  // The dock only renders while the workspace overlay is minimised (or absent);
  // a non-null minimised workspace means we show a restore button.
  const { workspace, restore: restoreWorkspace } = useVoiceWorkspace();
  const hasMinimizedWorkspace = Boolean(workspace?.minimized);
  const [draft, setDraft] = useState('');
  const drag = useVoiceDockDrag(isOpen);
  /** Dismissed Evidence callout key (`callId` or timeline `id`); cleared when a new entry arrives. */
  const [dismissedEvidenceKey, setDismissedEvidenceKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) setDismissedEvidenceKey(null);
  }, [isOpen]);

  // Compact "working on…" hint beside the status: the most recent running tool.
  const runningTool = isActive
    ? [...tools].reverse().find((tool) => tool.status === 'running')
    : undefined;
  const activityLabel = runningTool
    ? formatVoiceToolLabel(intl, runningTool.name, runningTool.title)
    : null;

  // Latest Evidence highlight for the dock stack (callId-merged in the store).
  // Paint policy (exclusive by default) lives in `../paint` — not a per-widget
  // consumer hook. Dual mode can allow dock + window together later.
  const paintShell = useVoicePaintShell();
  const latestEvidence = [...timeline]
    .reverse()
    .find((entry) => entry.kind === 'evidence');
  const evidenceKey =
    latestEvidence?.kind === 'evidence'
      ? latestEvidence.callId?.trim() || latestEvidence.id
      : null;
  const showEvidence =
    shouldPaint('evidence', 'dock', paintShell) &&
    latestEvidence?.kind === 'evidence' &&
    evidenceKey !== null &&
    evidenceKey !== dismissedEvidenceKey &&
    (Boolean(latestEvidence.text?.trim()) ||
      Boolean(latestEvidence.sources && latestEvidence.sources.length > 0));

  const handleDownloadDump = () => {
    const text = formatVoiceDebugDump(timeline, {
      exportedAt: new Date().toISOString(),
      connection,
      sessionState,
      surfacePath: Router.asPath,
      currentSessionId: getCurrentSessionId(),
      error: errorText,
    });
    downloadVoiceDebugDump(text);
  };

  const handleSendText = () => {
    const trimmed = draft.trim();
    if (!trimmed || !isActive) return;
    sendText(trimmed);
    setDraft('');
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <m.div
          key="voice-assistant-panel"
          role="complementary"
          // z-40: above page chrome, below Dialog/AlertDialog (z-50) so the
          // template picker and other modals are not buried under the dock.
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-end px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2"
          data-testid="voice-assistant-panel"
          aria-label={intl.formatMessage(messages.panelAriaLabel)}
          style={{ transformOrigin: 'bottom right' }}
          {...panelMotionProps(reduceMotion)}
        >
          <div
            className="pointer-events-auto flex w-[min(28rem,calc(100vw-1.5rem))] flex-col gap-2"
            style={{ transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)` }}
          >
            {errorText ? (
              <p
                className="text-pretty rounded-xl border border-validation-destructive/20 bg-validation-destructive/10 px-3 py-2 text-sm text-validation-destructive"
                role="alert"
              >
                {errorText}
              </p>
            ) : null}

            <VoiceMicPermissionDenied />

            {showEvidence && latestEvidence?.kind === 'evidence' && evidenceKey ? (
              <VoiceEvidenceCard
                text={latestEvidence.text}
                sources={latestEvidence.sources}
                onDismiss={() => setDismissedEvidenceKey(evidenceKey)}
              />
            ) : null}

            <div className="relative overflow-visible">
              <VoiceAssistantApprovalTopBar
                approval={approval}
                onResolve={resolveApproval}
              />

              <div className="relative z-10 rounded-2xl border border-border/60 bg-surface/95 shadow-xl backdrop-blur-md">
                <div className="flex items-center gap-0.5 border-b border-border/40 px-2.5 py-2">
                  <span className="relative flex size-6 shrink-0 items-center justify-center">
                    <HeidiLogoMark className="size-3.5 text-text-secondary" />
                    <span
                      className={cn(
                        'absolute end-0 top-0 size-1.5 rounded-full ring-2 ring-surface',
                        statusDotClass(connection),
                        isActive && !greetingComplete && 'animate-pulse'
                      )}
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0 flex-1 px-1">
                    <p className="truncate text-xs font-medium text-text-primary">
                      {intl.formatMessage(messages.title)}
                    </p>
                    <span
                      className="flex min-w-0 items-baseline gap-1"
                      data-testid="voice-assistant-status-line"
                    >
                      <VoiceAssistantTurnStatus
                        connection={connection}
                        sessionState={sessionState}
                        greetingComplete={greetingComplete}
                      />
                      {activityLabel ? (
                        <span
                          className="min-w-0 truncate text-[10px] text-text-tertiary"
                          data-testid="voice-assistant-activity-hint"
                        >
                          · {activityLabel}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {isProductFeedbackAvailable ? <VoiceFeedbackThumbs size="sm" /> : null}
                  <button
                    type="button"
                    className={cn(
                      'flex size-8 shrink-0 cursor-grab items-center justify-center rounded-lg',
                      'text-text-tertiary hover:bg-background-secondary hover:text-text-primary',
                      'active:cursor-grabbing',
                      'outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
                    )}
                    aria-label={intl.formatMessage(messages.dragHandle)}
                    onPointerDown={drag.onPointerDown}
                    onPointerMove={drag.onPointerMove}
                    onPointerUp={drag.onPointerUp}
                    onPointerCancel={drag.onPointerUp}
                    data-testid="voice-assistant-drag-handle"
                  >
                    <Hand className="size-3.5" />
                  </button>
                  {shouldAutoDumpVoiceSession() ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={intl.formatMessage(messages.downloadDump)}
                      onClick={handleDownloadDump}
                      disabled={timeline.length === 0}
                      className="size-8 shrink-0 rounded-lg text-text-tertiary hover:text-text-primary"
                    >
                      <Download className="size-3.5" />
                    </Button>
                  ) : null}
                  {hasMinimizedWorkspace ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={intl.formatMessage(voiceWorkspaceMessages.restore)}
                      onClick={restoreWorkspace}
                      data-testid="voice-assistant-expand"
                      className="size-8 shrink-0 rounded-lg text-text-tertiary hover:text-text-primary"
                    >
                      <ChevronsUpDown className="size-3.5" />
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={intl.formatMessage(messages.minimise)}
                    onClick={minimize}
                    data-testid="voice-assistant-minimise"
                    className="size-8 shrink-0 rounded-lg text-text-tertiary hover:text-text-primary"
                  >
                    <ChevronDown className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={intl.formatMessage(messages.endSession)}
                    onClick={handleEndClick}
                    data-testid="voice-assistant-end"
                    className="size-8 shrink-0 rounded-lg text-text-tertiary hover:text-text-primary"
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>

                <div className="p-2">
                  {dictation.active ? (
                    <VoiceDictationBar
                      dictation={dictation}
                      onStop={() => toggleDictation()}
                    />
                  ) : (
                    <VoiceAssistantComposer
                      draft={draft}
                      isActive={isActive}
                      muted={muted}
                      assistantSpeaking={assistantSpeaking}
                      micStream={micStream}
                      onDraftChange={setDraft}
                      onSend={handleSendText}
                      onToggleMute={toggleMute}
                      onStartDictation={
                        dictationEnabled ? () => toggleDictation() : undefined
                      }
                      embedded
                    />
                  )}
                </div>
              </div>
            </div>

            <VoiceAssistantEndDialog
              open={confirmEndOpen}
              onOpenChange={setConfirmEndOpen}
              onConfirm={close}
            />
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}
